<template>
  <div v-if="custodian" class="table-row">
    <div class="card">
      <div class="custom-checkbox">
        <CustomCheckbox
          :id="`custodian_${custodian.client.publicId.substring(0, 20)}`"
          :input-element="custodian"
        />
      </div>

      <div class="email">
        <div class="holder-info">
          <span>{{ custodian.client.email }}</span>
        </div>
      </div>

      <div class="screenshots">
        <div class="holder-info">
          <div class="toggle-save">
            <div class="toggleWrapper">
              <input
                :id="'s_screenshots_' + custodian.client.publicId.substring(0, 20)"
                v-model="custodian.configs.screenshotsConfig.value"
                type="checkbox"
                class="dn"
              >
              <label :for="'s_screenshots_' + custodian.client.publicId.substring(0, 20)" class="toggle">
                <span class="toggle__handler" />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="filtering">
        <div class="holder-info">
          <div class="toggle-save">
            <div class="toggleWrapper">
              <input
                :id="'s_filtering_' + custodian.client.publicId.substring(0, 20)"
                v-model="custodian.configs.prefilteringConfig.value"
                type="checkbox"
                class="dn"
              >
              <label :for="'s_filtering_' + custodian.client.publicId.substring(0, 20)" class="toggle">
                <span class="toggle__handler" />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div class="date">
        <div class="holder-info" :class="{ disabled: !custodian.configs.prefilteringConfig.value }">
          <date-picker
            :id="`date_range_` + custodian.client.publicId.substring(0, 20)"
            v-model="dataRange"
            range
            :format="'DD/MM/YYYY'"
            :range-separator="' - '"
            :editable="false"
            :clearable="false"
            class="datepicker-input"
            :input-class="'datepicker-input'"
            placeholder="Select Date range"
            :disabled-date="disableFutureDates"
          >
            <template v-if="formattedDateRange" #input>
              <div class="date-chosen">
                {{ formattedDateRange }}
              </div>
            </template>
            <template #icon-calendar>
              <CalendarIcon25 />
            </template>
            <template #icon-clear>
              <BlueCloseIcon25 />
            </template>
          </date-picker>
        </div>
      </div>

      <div class="sources">
        <div class="holder-info" :class="{ disabled: !custodian.configs.prefilteringConfig.value }">
          <CustomMultiSelect
            :id="`select_apps_` + custodian.client.publicId.substring(0, 20)"
            ref="appsSelect"
            v-model="appsChosen"
            :options="appsOptions"
            :multiple="true"
            placeholder="Choose apps"
            :preselected="preselectedApps"
            is-savable
            @saved="updateAppsList"
            @iconAction="iconSelectAction"
          />
        </div>
      </div>
    </div>
    <GalleryConfigPopup
      ref="galleryConfigPopup"
      :gallery-config="galleryConfig"
      @closed="onPopupClosed"
      @saveData="saveClientGalleryConfig"
    />
  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import CustomMultiSelect from '@/components/inputs/CustomMultiSelect.vue';
import capitalizeFirstLetter from '@/mixins/capitalizeFirstLetter';
import parseDate from '@/mixins/parseDate';
import CustomCheckbox from '@/components/inputs/CustomCheckbox.vue';
import debounce from '@/mixins/debounce';
import caseCustodianSettings from '@/mixins/caseSettings/caseCustodianSettings';
import {APP_LIST_MULTISELECT, CASE_CONFIG_ENUM} from '@/constants/const';
import CalendarIcon25 from '@/components/icons/CalendarIcon25.vue';
import BlueCloseIcon25 from '@/components/icons/BlueCloseIcon25.vue';
import GalleryConfigPopup from '@/components/popups/GalleryConfigPopup.vue';

export default {
  name: 'CustodianSettingsItem',
  components: {
    GalleryConfigPopup,
    BlueCloseIcon25,
    CalendarIcon25,
    CustomCheckbox,
    CustomMultiSelect,
    DatePicker,
  },
  emits: ['updateConfig', 'deleteConfig'],
  mixins: [
    capitalizeFirstLetter,
    parseDate,
    debounce,
    caseCustodianSettings,
  ],
  props: {
    custodian: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedDate: [],
      appsChosen: [],
      preselectedApps: [],
      dataRange: null,
      appsOptions: APP_LIST_MULTISELECT,
      initialConfig: null,
      clientConfig: {
        clientIds: [this.custodian.client.publicId],
        configs: [],
        email: this.custodian.client.email
      },
      galleryConfig: '',
    };
  },
  computed: {
    formattedDateRange() {
      if (this.selectedDate.length > 0) {
        const adjustedDateFrom = !isNaN(this.selectedDate[0]) ? new Date(this.selectedDate[0]) : null;
        const adjustedDateTo = !isNaN(this.selectedDate[1]) ? new Date(this.selectedDate[1]) : null;

        return `${this.parseDate(adjustedDateFrom)} - ${this.parseDate(adjustedDateTo)}`;
      } else {
        return null;
      }
    }
  },
  watch: {
    dataRange(value) {
      if (value) {
        const dateFrom = isNaN(Date.parse(value[0])) == false ? Date.parse(value[0]) : null;
        const dateTo = isNaN(Date.parse(value[1])) == false ? Date.parse(value[1]) : null;

        if (dateFrom && dateTo) {
          this.selectedDate = [dateFrom, dateTo];

          this.prepareDateConfig(this.clientConfig, dateFrom, dateTo);
          this.$emit('updateConfig', this.clientConfig);
        }
      }
    },
    'custodian.configs.prefilteringConfig.value'(value) {
      this.preparePrefilteringConfig(this.clientConfig, value);
      this.configAction(value);
    },
    'custodian.configs.screenshotsConfig.value'(val) {

      this.prepareScreenshotsConfig(this.clientConfig, val);
      this.$emit('updateConfig', this.clientConfig);
    },
  },
  created() {
    this.initialConfig = JSON.parse(JSON.stringify(this.custodian));
    const config = this.custodian.configs;

    if (!config.dateFromConfig.isDefault && config.dateFromConfig.value) {
      const adjustedDateTo = !isNaN(config.dateToConfig.value)
        ? Number(config.dateToConfig.value)
        : config.dateToConfig.value.toLowerCase() === 'now'
          ? Date.parse(new Date().toDateString())
          : config.dateToConfig.value;
      this.selectedDate = [Number(config.dateFromConfig.value), adjustedDateTo];
    }

    const preselectedAppsArray = !config.appConfig.isDefault && config.appConfig.value
      ? config.appConfig.value.split(',')
      : [];

    if (config.galleryConfig.value.includes('video') || config.galleryConfig.value.includes('photo')) {
      preselectedAppsArray.push('gallery');
    }

    this.preselectedApps = preselectedAppsArray.length > 0
      ? this.appsOptions.filter(item => preselectedAppsArray.includes(item.caseName.toLowerCase()))
      : [];

    this.galleryConfig = config.galleryConfig.value;
  },
  methods: {
    configAction(val) {
      if (val) {
        this.$emit('updateConfig', this.clientConfig);
      }
      if (val === false) {
        this.$emit('deleteConfig', this.clientConfig);
      }
    },
    disableFutureDates(date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return date > today;
    },
    saveClientGalleryConfig(galleryConfig) {
      if (galleryConfig && (galleryConfig.includes('video') || galleryConfig.includes('photo'))) {
        this.appsChosen.push({caseName: 'gallery', icon: 'GreyFilledSettingsIcon24'});
      } else {
        this.appsChosen = this.appsChosen.filter(item => item.caseName.toLowerCase() !== 'gallery');
      }
      this.clientConfig.configs = [
        {
          configName: CASE_CONFIG_ENUM.gallery,
          value: galleryConfig,
        }
      ];
      this.$emit('updateConfig', this.clientConfig);
    },
    updateAppsList() {
      if (this.appsChosen.length > 0) {
        const hasGallery = this.appsChosen.some(item => item.caseName.toLowerCase() === 'gallery');
        const galleryConfigExists = this.galleryConfig && (this.galleryConfig.includes('video') || this.galleryConfig.includes('photo'));

        this.clientConfig.configs = [
          {
            configName: CASE_CONFIG_ENUM.appList,
            value: this.appsChosen.map(item => {
              if (item.caseName.toLowerCase() !== 'gallery') {
                return item.caseName.replaceAll(' ', '').toLowerCase();
              }
            }).join(','),
          },
        ];

        if (hasGallery && !galleryConfigExists) {
          this.clientConfig.configs.push({
            configName: CASE_CONFIG_ENUM.gallery,
            value: 'photo',
          });
          this.galleryConfig = 'photo';
        }
      } else {
        this.clientConfig.configs = [
          {
            configName: CASE_CONFIG_ENUM.gallery,
            value: '',
          }
        ];
        this.galleryConfig = '';
      }
      this.$emit('updateConfig', this.clientConfig);
    },
    iconSelectAction(optionName) {
      switch (optionName) {
        case 'gallery':
          this.$refs.galleryConfigPopup.openPopups();
          break;
      }
    },
    onPopupClosed() {
      this.$refs.appsSelect.setPopupInactive();
    },
  }
};
</script>
<style scoped lang="scss">

.custom-checkbox {
  padding: 0 0 0 20px;
  width: 5%;
}

.email {
  width: 24.5%;
}

.screenshots {
  width: 11%;
}

.filtering {
  width: 15%;
}

.date {
  width: 22%;
}

.sources {
  width: 21%;
}

.contacts {
  width: 10%;
  padding: 0 5px;
}

.calls {
  width: 8%;
}

.card {
  display: flex;
  align-items: center;
  text-align: center;
  padding: 15px 0;
  transition: all 0.5s;
  position: relative;
  border-bottom: 1px solid var(--case-btn-border-color);
  width: 100%;
}

.holder-info {
  display: flex;
  align-items: center;
  margin: 0 0 0 15px;
  color: var(--secondary-text-color);

  .toggle-save {
    display: flex;
    width: 30%;
    justify-content: space-between;

    .toggleWrapper {
      input {
        position: absolute;
        left: -99em;
      }
    }

    .toggle {
      cursor: pointer;
      display: inline-block;
      position: relative;
      width: 38px;
      height: 18px;
      border-radius: 90px;
      background-color: var(--white-color);
      border: 1px solid var(--light-gray);
      transition: all 400ms;
    }

    .toggle__handler {
      display: inline-block;
      position: relative;
      z-index: 1;
      top: -.5px;
      left: -8px;
      width: 12px;
      height: 12px;
      border-radius: 50px;
      transition: all 400ms;
      background-color: var(--light-gray);
      transform: rotate(-50deg);
    }

    input:checked {
      + .toggle {
        background-color: var(--accent-color);
        border: 1px solid var(--accent-color);

        .toggle__handler {
          background-color: var(--white-color);
          transform: translate3d(16px, 0, 0) rotate(0);
        }
      }
    }
  }

  .datepicker-input {
    max-width: 160px;
    width: 100%;
  }
}

.date .holder-info,
.sources .holder-info {
  margin: 0;
}

.contacts .holder-info,
.calls .holder-info {
  margin: 0 0 0 5px;
}

.disabled {
  pointer-events: none;
  opacity: 0.4;
  transition: all .5s;
}

.visible {
  display: block;
}

.err-brd {
  border: 1px solid red;
  border-radius: 25px;
}

ul {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  li {
    list-style: disc;
  }
}

.date-chosen {
  width: 100%;
  max-width: 150px;
  height: 35px;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
  text-align: left;
  color: var(--secondary-text-color);
}

.datepicker-input > :first-child > :first-child {
  padding: 8.5px 27px 8.5px 13px;
}
</style>
